/**
 * useStopAllSessions -- React hook for bulk-stopping all active sessions for a user.
 *
 * Endpoint:
 * - POST /api/streaming/users/:serverType/:userId/sessions/stop-all (CSRF protected)
 *
 * Returns counts of stopped/failed sessions with per-session results.
 */
import { useCallback, useState } from "react";

import { getCsrfToken } from "../lib/csrf";

// ============================================================================
// Types
// ============================================================================

export interface StopAllResult {
  stopped: number;
  failed: number;
  results: Array<{ sessionId: string; ok: boolean; message?: string }>;
}

// ============================================================================
// useStopAllSessions
// ============================================================================

export function useStopAllSessions(): {
  stopping: boolean;
  stopAll: (serverType: string, userId: string) => Promise<StopAllResult | null>;
} {
  const [stopping, setStopping] = useState(false);

  const stopAll = useCallback(
    async (serverType: string, userId: string): Promise<StopAllResult | null> => {
      setStopping(true);
      try {
        const csrfToken = getCsrfToken();
        const url = `/api/streaming/users/${encodeURIComponent(serverType)}/${encodeURIComponent(userId)}/sessions/stop-all`;

        const res = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(csrfToken ? { "x-csrf-token": csrfToken } : {}),
          },
        });

        const json = await res.json();

        if (!res.ok || !json.ok || !json.data) {
          throw new Error(json.message ?? "Failed to stop user sessions");
        }

        return json.data as StopAllResult;
      } catch (err) {
        const message = err instanceof Error ? err.message : "Failed to stop user sessions";
        throw new Error(message);
      } finally {
        setStopping(false);
      }
    },
    []
  );

  return { stopping, stopAll };
}
